import { useField as useFormikField } from 'formik';
import React, { FC, FocusEventHandler, useState } from 'react';
import { TextField } from 'wix-ui-tpa/TextField';
import { useTranslation, useEnvironment } from '@wix/yoshi-flow-editor';

import { classes } from './Text.st.css';
import { Field, FieldWrapper } from '../../../../../../types';
import { FieldLabel } from '../../FieldLabel';
import { FieldPrivacyIcon } from '../../FieldPrivacyIcon';
import { DataHook } from '../../../../../../constants/DataHook';
import { getFieldLabel } from '../utils/getFieldLabel';

type ErrorType = { value: string } | undefined;

interface TextProps extends FieldWrapper {
  pattern?: HTMLInputElement['pattern'];
  type?: HTMLInputElement['type'];
  onBlur?: FocusEventHandler<HTMLInputElement>;
}

export const Text: FC<TextProps> = ({
  theme,
  onBlur,
  formikFieldSelector,
  type,
  pattern,
}) => {
  const { t } = useTranslation();
  const { multilingual } = useEnvironment();
  const [isClearButtonVisible, setClearButtonVisibility] = useState(false);
  const [formikField, meta, helpers] =
    useFormikField<Field>(formikFieldSelector);

  const error = (meta.error as unknown as ErrorType)?.value;
  const field = formikField.value;
  const { required, infoTooltipText, value, key, id } = field;

  return (
    <div data-hook={key}>
      <FieldLabel
        text={getFieldLabel(t, field, multilingual.isEnabled)}
        htmlFor={id}
        isFieldRequired={required}
        infoTooltipText={infoTooltipText}
      />
      <FieldPrivacyIcon
        formikFieldSelector={formikFieldSelector}
        data-hook={DataHook.FieldPrivacyIcon}
      >
        <TextField
          id={id}
          className={classes.field}
          data-hook={DataHook.TextField}
          value={
            value ??
            '' /* Need to pass empty string, for formik form reset to work*/
          }
          theme={theme}
          type={type ?? 'text'}
          required={required}
          pattern={pattern}
          errorMessage={error}
          withClearButton={isClearButtonVisible}
          error={!!error}
          onClear={() => helpers.setValue({ ...field, value: '' })}
          onChange={(event) => {
            helpers.setValue({
              ...field,
              value: event.target.value,
            });
            setClearButtonVisibility(!!event.target.value);
          }}
          onFocus={() => setClearButtonVisibility(!!value)}
          onBlur={(event) => {
            setTimeout(() => setClearButtonVisibility(false), 100);
            onBlur?.(event);
          }}
          errorTooltipMaxWidth={2000}
        />
      </FieldPrivacyIcon>
    </div>
  );
};
